import { useEffect, useMemo, useState, useRef } from 'react';

import {
  getTeamAssignmentsQueryKeyPrefix,
  TeamAssignmentsRow,
  useGetTeamAssignments,
} from '@frontend/api';
import {
  AvatarWithName,
  Box,
  Center,
  HStack,
  Pagination,
  PercentageBreakdown,
  PercentageBreakdownBar,
  PercentageBreakdownTooltip,
  Spinner,
  TableContainer,
  TableV2,
  Tbody,
  Td,
  Tr,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@frontend/components';

import {
  DefaultTableShadowOverlay,
  StickyElementRefs,
} from 'components/listTablePage';

import { paginationStatus } from 'utils/stringUtils';

import {
  Pane,
  PaneHeader,
  PaneTitle,
  PaneBody,
  PaneSubtitle,
} from '../components/pane';

const ROWS_PER_PAGE = 20;
const DRAFT_COLOR = 'status.notStarted';
const OUTSTANDING_COLOR = 'alert.success';

type TeamAssignmentsTabKey =
  | 'lineItemEstimator'
  | 'quoteEstimator'
  | 'quoteSalesperson';

const TABS: { key: TeamAssignmentsTabKey; label: string }[] = [
  { key: 'lineItemEstimator', label: 'Line Item Estimator' },
  { key: 'quoteEstimator', label: 'Quote Estimator' },
  { key: 'quoteSalesperson', label: 'Quote Salesperson' },
];

const EMPTY_STICKY_REFS: StickyElementRefs = {};

const AssignmentBreakdownBar = ({
  draftCount,
  outstandingCount,
  maxTotal,
}: {
  draftCount: number;
  outstandingCount: number;
  maxTotal: number;
}) => {
  const breakdownData = [
    {
      color: DRAFT_COLOR,
      label: 'Draft',
      value: draftCount,
      displayValue: String(draftCount),
    },
    {
      color: OUTSTANDING_COLOR,
      label: 'Outstanding',
      value: outstandingCount,
      tiled: true,
      displayValue: String(outstandingCount),
    },
  ];
  return (
    <VStack align="stretch" width="full" gap={0.5}>
      <Box>
        <PercentageBreakdown
          data={breakdownData}
          variant="count"
          maxTotal={maxTotal}
        >
          <PercentageBreakdownTooltip>
            <PercentageBreakdownBar width="100%" />
          </PercentageBreakdownTooltip>
        </PercentageBreakdown>
      </Box>
    </VStack>
  );
};

const TeamAssignmentsTable = ({
  rows,
  maxTotal,
}: {
  rows: TeamAssignmentsRow[];
  maxTotal: number;
}) => {
  return (
    <TableContainer
      border="none"
      borderRadius="none"
      variant="simple"
      zebra="odd"
    >
      <TableV2>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={`${row.user?.id ?? 'unassigned'}-${row.draftCount}-${
                row.outstandingCount
              }`}
            >
              <Td width={26} py={1}>
                <AvatarWithName
                  name={
                    row.user
                      ? row.user.isDeleted
                        ? row.user.email
                        : [row.user.firstName, row.user.lastName]
                            .filter(Boolean)
                            .join(' ') || undefined
                      : undefined
                  }
                  isDeleted={row.user?.isDeleted ?? false}
                  backgroundColor={row.user?.avatarColor}
                  textProps={{ noOfLines: 1 }}
                />
              </Td>
              <Td py={1}>
                <AssignmentBreakdownBar
                  draftCount={row.draftCount}
                  outstandingCount={row.outstandingCount}
                  maxTotal={maxTotal}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </TableV2>
    </TableContainer>
  );
};

const TeamAssignmentsTabs = ({
  activeGroupIds,
}: {
  activeGroupIds: number[];
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const { data, isLoading } = useGetTeamAssignments({
    query: activeGroupIds.length > 0 ? { groups: activeGroupIds } : undefined,
    queryOptions: {
      useErrorBoundary: true,
    },
  });
  const [pages, setPages] = useState<Record<TeamAssignmentsTabKey, number>>({
    lineItemEstimator: 1,
    quoteEstimator: 1,
    quoteSalesperson: 1,
  });

  const tabRows: Record<TeamAssignmentsTabKey, TeamAssignmentsRow[]> = useMemo(
    () => ({
      lineItemEstimator: data?.lineItemEstimator ?? [],
      quoteEstimator: data?.quoteEstimator ?? [],
      quoteSalesperson: data?.quoteSalesperson ?? [],
    }),
    [data],
  );
  const emptyUnassignedRow = useMemo(
    () =>
      ({
        user: null,
        draftCount: 0,
        outstandingCount: 0,
      } satisfies TeamAssignmentsRow),
    [],
  );

  useEffect(() => {
    setPages((current) => {
      const next = { ...current };
      let changed = false;
      TABS.forEach(({ key }) => {
        const maxPage = Math.max(
          1,
          Math.ceil(tabRows[key].length / ROWS_PER_PAGE),
        );
        if (current[key] > maxPage) {
          next[key] = maxPage;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [tabRows]);

  if (isLoading) {
    return (
      <Center height="full">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Tabs
      orientation="horizontal"
      size="md"
      display="flex"
      flexDirection="column"
      height="100%"
      minHeight={0}
      index={tabIndex}
      onChange={setTabIndex}
    >
      <TabList>
        {TABS.map((tab) => (
          <Tab key={tab.key}>{tab.label}</Tab>
        ))}
      </TabList>
      <TabPanels flex={1} minHeight={0} display="flex" flexDirection="column">
        {TABS.map((tab) => {
          const rows = tabRows[tab.key];
          const displayRows = rows.length > 0 ? rows : [emptyUnassignedRow];
          const maxTotal = Math.max(
            1,
            ...displayRows.map((row) => row.draftCount + row.outstandingCount),
          );
          const currentPage = pages[tab.key];
          const pagedRows = displayRows.slice(
            (currentPage - 1) * ROWS_PER_PAGE,
            currentPage * ROWS_PER_PAGE,
          );
          return (
            <TabPanel
              key={tab.key}
              p={0}
              flex={1}
              minHeight={0}
              display="flex"
              flexDirection="column"
            >
              <Box position="relative" flex={1} display="flex" minHeight={0}>
                <DefaultTableShadowOverlay
                  overflowBoxRef={scrollRef}
                  stickyElementRefs={EMPTY_STICKY_REFS}
                  key={tabIndex}
                />
                <Box
                  ref={scrollRef}
                  flex={1}
                  minHeight={0}
                  overflowY="auto"
                  bg="page.plateLight"
                >
                  <TeamAssignmentsTable rows={pagedRows} maxTotal={maxTotal} />
                </Box>
              </Box>
              <HStack
                justifyContent="flex-end"
                spacing={2}
                px={2}
                minH={4.5}
                flexShrink={0}
                borderTop="1px solid"
                borderColor="separator.divider"
              >
                <HStack spacing={0.5}>
                  <Box width={1.5} height={1.5} bg={DRAFT_COLOR} />
                  <Text fontSize="sm" fontWeight="medium">
                    Draft
                  </Text>
                </HStack>
                <HStack spacing={0.5}>
                  <Box
                    width={1.5}
                    height={1.5}
                    bg={OUTSTANDING_COLOR}
                    border="1px solid"
                    borderColor={OUTSTANDING_COLOR}
                    backgroundImage="repeating-linear-gradient(135deg, transparent, transparent 1px, rgba(255,255,255,0.8) 1px, rgba(255,255,255,0.8) 3px)"
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    Outstanding
                  </Text>
                </HStack>
              </HStack>
              {displayRows.length > ROWS_PER_PAGE ? (
                <HStack
                  justifyContent="space-between"
                  h={6}
                  px={2}
                  flexShrink={0}
                  borderTop="1px solid"
                  borderTopColor="separator.divider"
                >
                  <Text>
                    {paginationStatus(
                      currentPage,
                      ROWS_PER_PAGE,
                      displayRows.length,
                      'team member',
                    )}
                  </Text>
                  <Pagination
                    totalItems={displayRows.length}
                    itemsPerPage={ROWS_PER_PAGE}
                    currentPage={currentPage}
                    onChangePage={(page) =>
                      setPages((current) => ({ ...current, [tab.key]: page }))
                    }
                  />
                </HStack>
              ) : null}
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};

export type TeamAssignmentsPaneProps = {
  activeGroupIds: number[];
};

export const TeamAssignmentsPane = ({
  activeGroupIds,
}: TeamAssignmentsPaneProps) => {
  const websocketOptions = useMemo(() => {
    return {
      topic: activeGroupIds.map((id) => `${id}.orders.created`),
    };
  }, [activeGroupIds]);

  return (
    <Pane
      refetchQueryKey={getTeamAssignmentsQueryKeyPrefix()}
      websocketOptions={websocketOptions}
    >
      <PaneHeader>
        <PaneTitle>Team Assignments</PaneTitle>
        <PaneSubtitle>Created in the last 90 days</PaneSubtitle>
      </PaneHeader>
      <PaneBody display="flex" flexDirection="column" minHeight={0}>
        <TeamAssignmentsTabs activeGroupIds={activeGroupIds} />
      </PaneBody>
    </Pane>
  );
};
